import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { Stage, STAGE_OPEN_GESTURE } from '@cosmos/shared';
import {
  MeasurementAction,
  MeasurementActionsMenuComponent,
} from '../../components/measurement-actions-menu/measurement-actions-menu.component';

// MeasurementAction.labelKey strings are translated inside
// MeasurementActionsMenuComponent itself — this component never calls
// TranslateService directly, only TranslatePipe in its own template.
@Component({
  selector: 'cosmos-folder-card',
  imports: [TranslatePipe, CardModule, MeasurementActionsMenuComponent],
  templateUrl: './folder-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .folder-card ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow);
      transition: box-shadow 150ms ease;
    }
    .folder-card:hover ::ng-deep .p-card {
      box-shadow: var(--p-card-shadow), 0 0 0 1px var(--p-surface-300);
    }
    .folder-card--selected ::ng-deep .p-card {
      box-shadow: 0 0 0 2px var(--p-primary-color) !important;
    }
  `,
})
export class FolderCardComponent {
  readonly stage = input.required<Stage>();
  readonly layout = input<'grid' | 'list'>('grid');
  readonly selected = input<boolean>(false);
  readonly canEdit = input<boolean>(false);
  /** List mode: whether this is the last row in the list (drop the bottom rule). */
  readonly isLast = input<boolean>(false);
  /** List mode: absolute first row in the card — round the selected bg top. */
  readonly isFirst = input<boolean>(false);
  /** List mode: absolute last row in the card — round the selected bg bottom. */
  readonly isAbsoluteLast = input<boolean>(false);

  readonly open = output<void>();
  readonly select = output<void>();
  readonly rename = output<void>();
  readonly move = output<void>();
  // Stage deletion is delegated to the f-admin app — not exposed here.

  /** Instance field (not the static import) so tests can override it
   *  per-instance; defaults to the real STAGE_OPEN_GESTURE ('single' at
   *  launch — single-click opens, no selection). */
  protected openGesture: 'single' | 'double' = STAGE_OPEN_GESTURE;

  protected readonly measurementCount = computed(() => this.stage().number_measurements ?? 0);

  protected readonly kebabActions = computed<MeasurementAction[]>(() => [
    { labelKey: 'stages.action.rename', icon: 'fa-solid fa-pen', action: () => this.rename.emit() },
    { labelKey: 'stages.action.move', icon: 'fa-solid fa-arrows-turn-to-dots', action: () => this.move.emit() },
  ]);

  onClick(): void {
    if (this.openGesture === 'single') {
      this.open.emit();
    } else {
      this.select.emit();
    }
  }

  onDoubleClick(): void {
    if (this.openGesture === 'double') {
      this.open.emit();
    }
    // 'single' mode: double-click is a no-op — the single click already opened.
  }
}
